import { Helmet } from "react-helmet-async";
import { useState, useEffect } from "react";
import { QRCodeSVG } from "qrcode.react";
import { motion, AnimatePresence, useReducedMotion } from "framer-motion";
import { Phone, ArrowUpRight, Sparkles, Check, Loader2 } from "lucide-react";
import { getCanonicalUrl } from "@/config/site";
import { createOrganizationSchema, createSoftwareApplicationSchema } from "@/config/seo";
import { trackCallClick } from "@/lib/ga4";
import type { GenieIconName } from "@/components/ui/genie-icons/genie-icons";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

const DEMO_AGENT_URL = "business-onboarding";
const LIVE_AGENT_BASE = "/live-agent";
const DEMO_PHONE_NUMBER = "+1 (443) 232-1464";

// Brand colors from design system
const MAGENTA_BEAM = "#FF4DB8"; // Secondary - accents, voice indicators
const SPARK_BLUE = "#4E9CFF"; // Primary - main brand, buttons, links
const NIGHT = "#0F1834"; // Immersive stage background (matches Meet-the-Genie)
const BRAND_GRADIENT = `linear-gradient(135deg, ${SPARK_BLUE} 0%, ${MAGENTA_BEAM} 100%)`;

// Widget configuration
const WIDGET_CONFIG = {
  agentUrl: "business-onboarding",
  position: "bottom-right",
  primaryColor: SPARK_BLUE,
  buttonColor: "#EBF5FF",
  baseUrl: "https://helpgenie.ai",
  agentName: "Help Genie for Business",
  customAvatarUrl:
    "https://vdskjkjxyeehklwxnhrf.supabase.co/storage/v1/object/public/genie-images/6q7i3if-1757306079351.png",
  buttonText: "Let's chat!",
};

type OptionKey = "live-link" | "phone" | "qr-code" | "web-embed";

declare global {
  interface Window {
    hg: ((action: string, config?: Record<string, unknown>) => void) & { q?: unknown[] };
  }
}

// ─── Ambient background — drifting brand aurora on a night stage ───────────────
const AmbientBackground = ({ reduce }: { reduce: boolean }) => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden">
    <motion.div
      className="absolute -top-1/4 -left-1/4 h-[70vmax] w-[70vmax] rounded-full blur-[120px]"
      style={{ background: `radial-gradient(circle, ${SPARK_BLUE}40 0%, transparent 60%)` }}
      animate={reduce ? undefined : { x: [0, 60, 0], y: [0, 40, 0], scale: [1, 1.1, 1] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
    />
    <motion.div
      className="absolute -bottom-1/4 -right-1/4 h-[70vmax] w-[70vmax] rounded-full blur-[120px]"
      style={{ background: `radial-gradient(circle, ${MAGENTA_BEAM}38 0%, transparent 60%)` }}
      animate={reduce ? undefined : { x: [0, -50, 0], y: [0, -40, 0], scale: [1.1, 1, 1.1] }}
      transition={{ duration: 22, repeat: Infinity, ease: "easeInOut" }}
    />
    {/* Vignette for legibility */}
    <div
      className="absolute inset-0"
      style={{
        background:
          "radial-gradient(120% 90% at 50% 30%, transparent 40%, rgba(15,24,52,0.55) 100%)",
      }}
    />
  </div>
);

// ─── Genie orb — a listening voice presence you can tap to start ──────────────
const GenieOrb = ({ reduce, onClick }: { reduce: boolean; onClick: () => void }) => (
  <button
    onClick={onClick}
    aria-label="Start a live conversation with a Genie"
    className="group relative flex h-40 w-40 items-center justify-center rounded-full outline-none sm:h-48 sm:w-48"
  >
    {/* Sonar rings — voice pings radiating outward */}
    {!reduce &&
      [0, 1, 2].map((i) => (
        <motion.span
          key={i}
          className="absolute inset-0 rounded-full border-2"
          style={{ borderColor: i % 2 === 0 ? `${SPARK_BLUE}70` : `${MAGENTA_BEAM}70` }}
          initial={{ scale: 0.85, opacity: 0.55 }}
          animate={{ scale: 1.9, opacity: 0 }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeOut", delay: i }}
        />
      ))}

    {/* Rotating conic halo */}
    <motion.span
      className="absolute inset-2 rounded-full blur-md"
      style={{
        background: `conic-gradient(from 0deg, ${SPARK_BLUE}, ${MAGENTA_BEAM}, ${SPARK_BLUE})`,
        opacity: 0.55,
      }}
      animate={reduce ? undefined : { rotate: 360 }}
      transition={{ duration: 12, repeat: Infinity, ease: "linear" }}
    />

    {/* Core badge */}
    <motion.span
      className="relative z-10 flex h-24 w-24 items-center justify-center rounded-full bg-white shadow-2xl sm:h-28 sm:w-28"
      style={{ boxShadow: `0 0 44px ${SPARK_BLUE}66, 0 0 72px ${MAGENTA_BEAM}44` }}
      animate={reduce ? undefined : { scale: [1, 1.05, 1] }}
      transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
    >
      <img src="/helpgenie_logo.png" alt="Help Genie" className="h-12 w-12 sm:h-14 sm:w-14" />
    </motion.span>

    {/* Tap hint on hover */}
    <span className="absolute -bottom-9 left-1/2 -translate-x-1/2 whitespace-nowrap text-xs font-medium text-white/60 opacity-0 transition-opacity group-hover:opacity-100">
      Tap to start talking →
    </span>
  </button>
);

// ─── Channel card — glassmorphic selectable connection method ─────────────────
interface ChannelCardProps {
  genieIcon: GenieIconName;
  title: string;
  description: string;
  onClick: () => void;
  isActive: boolean;
  external?: boolean;
}

const ChannelCard = ({ genieIcon, title, description, onClick, isActive, external }: ChannelCardProps) => (
  <motion.button
    onClick={onClick}
    variants={{
      hidden: { opacity: 0, y: 18 },
      show: { opacity: 1, y: 0 },
    }}
    whileHover={{ y: -4 }}
    whileTap={{ scale: 0.98 }}
    className="group relative flex flex-col items-center overflow-hidden rounded-2xl p-5 text-center backdrop-blur-md transition-colors"
    style={{
      background: isActive ? "rgba(255,255,255,0.14)" : "rgba(255,255,255,0.06)",
      border: `1px solid ${isActive ? "transparent" : "rgba(255,255,255,0.14)"}`,
      backgroundClip: isActive ? "padding-box" : undefined,
      boxShadow: isActive ? `0 0 0 1.5px transparent` : undefined,
    }}
  >
    {/* Gradient border when active */}
    {isActive && (
      <span
        className="pointer-events-none absolute inset-0 rounded-2xl"
        style={{ padding: 1, background: BRAND_GRADIENT, WebkitMask: "linear-gradient(#000 0 0) content-box, linear-gradient(#000 0 0)", WebkitMaskComposite: "xor", maskComposite: "exclude" }}
      />
    )}

    {external && (
      <ArrowUpRight className="absolute right-3 top-3 h-4 w-4 text-white/40 transition-colors group-hover:text-white/80" />
    )}
    {isActive && !external && (
      <span
        className="absolute right-3 top-3 flex h-5 w-5 items-center justify-center rounded-full"
        style={{ background: BRAND_GRADIENT }}
      >
        <Check className="h-3 w-3 text-white" />
      </span>
    )}

    <div
      className="mb-3 flex h-14 w-14 items-center justify-center rounded-xl transition-transform group-hover:scale-105"
      style={{ background: `linear-gradient(135deg, ${SPARK_BLUE}26, ${MAGENTA_BEAM}26)` }}
    >
      <GenieIcon
        name={genieIcon}
        size={34}
        color={SPARK_BLUE}
        secondaryColor={MAGENTA_BEAM}
        gradientEnabled
        gradientAngle={135}
        label={title}
      />
    </div>
    <h3 className="text-base font-semibold text-white">{title}</h3>
    <p className="mt-1 text-sm leading-snug text-white/60">{description}</p>
  </motion.button>
);

// ─── Reveal panel shell ───────────────────────────────────────────────────────
const RevealPanel = ({ children }: { children: React.ReactNode }) => (
  <motion.div
    initial={{ opacity: 0, y: 16, height: 0 }}
    animate={{ opacity: 1, y: 0, height: "auto" }}
    exit={{ opacity: 0, y: -12, height: 0 }}
    transition={{ duration: 0.35, ease: "easeInOut" }}
    className="w-full overflow-hidden"
  >
    <div
      className="mx-auto mt-6 max-w-md rounded-2xl border border-white/15 bg-white/[0.07] p-6 text-center backdrop-blur-xl"
      style={{ boxShadow: `0 0 40px ${SPARK_BLUE}22` }}
    >
      {children}
    </div>
  </motion.div>
);

const TalkToGeniePage = () => {
  const [activeOption, setActiveOption] = useState<OptionKey | null>(null);
  const [widgetLoaded, setWidgetLoaded] = useState(false);
  const reduce = !!useReducedMotion();

  const liveAgentUrl = `${window.location.origin}${LIVE_AGENT_BASE}/${DEMO_AGENT_URL}`;
  const description =
    "Try Help Genie through a live link, phone call, QR code or web embed and experience the voice AI journey yourself.";
  const organizationSchema = createOrganizationSchema();
  const softwareSchema = createSoftwareApplicationSchema(
    "Talk to Genie",
    description,
    getCanonicalUrl("/talk-to-genie")
  );

  // Load widget script when web-embed is selected
  useEffect(() => {
    if (activeOption === "web-embed" && !widgetLoaded) {
      window.hg =
        window.hg ||
        function (...args: unknown[]) {
          (window.hg.q = window.hg.q || []).push(args);
        };
      window.hg.q = window.hg.q || [];

      const script = document.createElement("script");
      script.id = "hg";
      script.src = "https://helpgenie.ai/widget.js";
      script.async = true;
      script.onload = () => {
        window.hg("init", WIDGET_CONFIG);
        setWidgetLoaded(true);
      };

      const firstScript = document.getElementsByTagName("script")[0];
      if (firstScript?.parentNode) {
        firstScript.parentNode.insertBefore(script, firstScript);
      }
    }
  }, [activeOption, widgetLoaded]);

  // Cleanup widget when leaving page
  useEffect(() => {
    return () => {
      const widgetElements = document.querySelectorAll('[id^="hg-"], [class^="hg-"]');
      widgetElements.forEach((el) => el.remove());
      const script = document.getElementById("hg");
      if (script) script.remove();
    };
  }, []);

  const goLive = () => {
    window.location.href = `${LIVE_AGENT_BASE}/${DEMO_AGENT_URL}`;
  };

  const handleOptionClick = (option: OptionKey) => {
    if (option === "live-link") {
      goLive();
      return;
    }
    setActiveOption(activeOption === option ? null : option);
  };

  const options = [
    {
      key: "live-link" as OptionKey,
      genieIcon: "chat" as const,
      title: "Live Link",
      description: "Open the live conversation page",
      external: true,
    },
    {
      key: "phone" as OptionKey,
      genieIcon: "phone" as const,
      title: "Phone",
      description: "Call and talk to the Genie",
    },
    {
      key: "qr-code" as OptionKey,
      genieIcon: "qr-code" as const,
      title: "QR Code",
      description: "Scan with your phone",
    },
    {
      key: "web-embed" as OptionKey,
      genieIcon: "code" as const,
      title: "Web Embed",
      description: "See the widget in action",
    },
  ];

  return (
    <>
      <Helmet>
        <title>Talk to Genie | Live Demo of Help Genie</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={getCanonicalUrl("/talk-to-genie")} />
        <meta property="og:title" content="Talk to Genie | Live Demo of Help Genie" />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={getCanonicalUrl("/talk-to-genie")} />
        <meta property="og:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Talk to Genie | Live Demo of Help Genie" />
        <meta name="twitter:description" content={description} />
        <meta name="twitter:image" content={getCanonicalUrl("/helpgenie_logo.png")} />
        <script type="application/ld+json">{JSON.stringify(softwareSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      </Helmet>

      <div
        className="relative min-h-screen w-full overflow-x-hidden text-white"
        style={{ backgroundColor: NIGHT }}
      >
        <AmbientBackground reduce={reduce} />

        {/* Top gradient ribbon */}
        <div className="absolute inset-x-0 top-0 z-20 h-1.5" style={{ background: BRAND_GRADIENT }} />

        {/* Wordmark */}
        <div className="absolute left-6 top-5 z-20 sm:left-10 sm:top-7">
          <img
            src="/branding/helpgenie/helpgenie-wordmark.png"
            alt="Help Genie"
            className="h-7 w-auto sm:h-8"
            style={{ filter: "brightness(0) invert(1)" }}
          />
        </div>

        {/* Home pill */}
        <a
          href="/"
          className="absolute right-6 top-5 z-20 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-4 py-2 text-sm font-medium text-white/90 backdrop-blur transition-all hover:bg-white/20 sm:right-10 sm:top-7"
        >
          ← Home
        </a>

        {/* Stage */}
        <div className="relative z-10 mx-auto flex min-h-screen max-w-4xl flex-col items-center justify-center px-4 py-24">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: "easeOut" }}
          >
            <GenieOrb reduce={reduce} onClick={goLive} />
          </motion.div>

          <motion.div
            className="mt-12 text-center"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
          >
            <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/80 backdrop-blur">
              <Sparkles className="h-3.5 w-3.5" style={{ color: MAGENTA_BEAM }} />
              Live demo
            </div>
            <h1 className="text-4xl font-extrabold leading-tight tracking-tight sm:text-5xl">
              <span
                className="bg-clip-text text-transparent"
                style={{ backgroundImage: BRAND_GRADIENT, WebkitBackgroundClip: "text" }}
              >
                Talk to a Genie
              </span>
            </h1>
            <p className="mx-auto mt-3 max-w-lg text-base text-white/70 sm:text-lg">
              Four ways to connect. Pick one and start a real conversation, live voice or chat,
              answering in seconds.
            </p>
          </motion.div>

          {/* Channel cards */}
          <motion.div
            className="mt-10 grid w-full grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4"
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08, delayChildren: 0.25 } } }}
            initial="hidden"
            animate="show"
          >
            {options.map((option) => (
              <ChannelCard
                key={option.key}
                genieIcon={option.genieIcon}
                title={option.title}
                description={option.description}
                onClick={() => handleOptionClick(option.key)}
                isActive={activeOption === option.key}
                external={option.external}
              />
            ))}
          </motion.div>

          {/* Reveal area */}
          <AnimatePresence mode="wait">
            {activeOption === "phone" && (
              <RevealPanel key="phone">
                <p className="text-sm text-white/60">Call this number to talk to a Genie</p>
                <a
                  href={`tel:${DEMO_PHONE_NUMBER.replace(/\D/g, "")}`}
                  onClick={() => trackCallClick("talk_to_genie_page")}
                  className="mt-2 block bg-clip-text text-3xl font-extrabold text-transparent sm:text-4xl"
                  style={{ backgroundImage: BRAND_GRADIENT, WebkitBackgroundClip: "text" }}
                >
                  {DEMO_PHONE_NUMBER}
                </a>
                <a
                  href={`tel:${DEMO_PHONE_NUMBER.replace(/\D/g, "")}`}
                  onClick={() => trackCallClick("talk_to_genie_page")}
                  className="mt-4 inline-flex items-center gap-2 rounded-xl px-6 py-3 text-sm font-semibold text-white shadow-lg transition-all hover:-translate-y-0.5 hover:opacity-95"
                  style={{ background: BRAND_GRADIENT }}
                >
                  <Phone className="h-4 w-4" />
                  Call now
                </a>
                <p className="mt-3 text-xs text-white/45">Answers in seconds · live voice</p>
              </RevealPanel>
            )}

            {activeOption === "qr-code" && (
              <RevealPanel key="qr-code">
                <p className="text-sm text-white/60">Scan with your phone to open the live conversation</p>
                <div className="mx-auto mt-4 inline-block rounded-2xl bg-white p-4 shadow-lg" style={{ boxShadow: `0 0 32px ${MAGENTA_BEAM}44` }}>
                  <QRCodeSVG value={liveAgentUrl} size={168} level="M" includeMargin={false} fgColor="#222B45" />
                </div>
                <p className="mt-4 text-xs text-white/45">
                  Opens the live conversation on your mobile device
                </p>
              </RevealPanel>
            )}

            {activeOption === "web-embed" && (
              <RevealPanel key="web-embed">
                <div className="flex items-center justify-center gap-2 text-white">
                  {!widgetLoaded && <Loader2 className="h-4 w-4 animate-spin" style={{ color: SPARK_BLUE }} />}
                  <p className="text-sm font-medium">
                    {widgetLoaded
                      ? "The widget is live. Look in the bottom-right corner."
                      : "Loading widget..."}
                  </p>
                </div>
                <p className="mt-2 text-xs text-white/45">
                  This is exactly how the Genie appears when embedded on your website
                </p>
              </RevealPanel>
            )}
          </AnimatePresence>
        </div>
      </div>
    </>
  );
};

export default TalkToGeniePage;
